import { useEffect, useState } from "react";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import {
  getZohoItemMappingSuggestionsURL,
  saveZohoItemMappingURL,
  createAndMapZohoItemURL,
  deleteZohoItemMappingURL,
} from "../../../utils/urls";

const STATUS = {
  mapped: { label: "Mapped", cls: "bg-success-subtle text-success" },
  suggested: { label: "Suggested", cls: "bg-warning-subtle text-warning" },
  unmatched: { label: "Not mapped", cls: "bg-danger-subtle text-danger" },
  stale: { label: "Item missing in Zoho", cls: "bg-danger-subtle text-danger" },
};

const errorOf = (res, fallback) => res?.data?.error || res?.error || fallback;

const ZohoItemMapping = ({ facilityId }) => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selections, setSelections] = useState({});
  const [busy, setBusy] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  // category -> { name, rate, accountId } while the "create in Zoho" form is open
  const [creating, setCreating] = useState({});

  const load = async () => {
    try {
      setLoading(true);
      const res = await makeRequest2(getZohoItemMappingSuggestionsURL(facilityId), "GET");
      if (res?.success && res.data?.success) {
        const { categories: rows, zohoItems, accounts: accs } = res.data.data;
        setCategories(rows || []);
        setItems(zohoItems || []);
        setAccounts(accs || []);
        const sel = {};
        (rows || []).forEach((r) => {
          sel[r.category] = r.mapping?.zohoItemId || r.suggestion?.zohoItemId || "";
        });
        setSelections(sel);
      } else {
        toastify("error", errorOf(res, "Could not load items from Zoho Books"));
      }
    } catch (e) {
      toastify("error", "Could not load items from Zoho Books");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (facilityId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  const save = async (category, zohoItemId, matchType = "manual") => {
    const res = await makeRequest2(saveZohoItemMappingURL(facilityId), "POST", { category, zohoItemId, matchType });
    if (!(res?.success && res.data?.success)) throw new Error(errorOf(res, "Failed to save mapping"));
  };

  const handleSave = async (row) => {
    const id = selections[row.category];
    if (!id) return toastify("error", "Choose a Zoho item first");
    setBusy(row.category);
    try {
      await save(row.category, id, row.suggestion?.zohoItemId === id && !row.mapping ? "auto" : "manual");
      toastify("success", `${row.categoryLabel} mapped`);
      await load();
    } catch (e) {
      toastify("error", e.message);
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async (row) => {
    if (!window.confirm(`Remove the Zoho item mapping for ${row.categoryLabel}?`)) return;
    setBusy(row.category);
    try {
      const res = await makeRequest2(deleteZohoItemMappingURL(facilityId, row.category), "DELETE");
      if (!(res?.success && res.data?.success)) throw new Error(errorOf(res, "Failed to remove mapping"));
      await load();
    } catch (e) {
      toastify("error", e.message);
    } finally {
      setBusy(null);
    }
  };

  const handleAcceptAll = async () => {
    const todo = categories.filter((r) => r.status === "suggested");
    if (!todo.length) return toastify("info", "No suggestions to accept");
    setBulkBusy(true);
    let ok = 0;
    for (const r of todo) {
      try {
        await save(r.category, r.suggestion.zohoItemId, "auto");
        ok += 1;
      } catch (e) {
        toastify("error", `${r.categoryLabel}: ${e.message}`);
      }
    }
    setBulkBusy(false);
    if (ok) toastify("success", `${ok} mapping(s) saved`);
    load();
  };

  const openCreate = (row) =>
    setCreating((c) => ({ ...c, [row.category]: { name: row.categoryLabel, rate: 0, accountId: accounts[0]?.accountId || "" } }));

  const handleCreate = async (row) => {
    const form = creating[row.category];
    if (!form?.name?.trim()) return toastify("error", "Item name is required");
    setBusy(row.category);
    try {
      const res = await makeRequest2(createAndMapZohoItemURL(facilityId), "POST", {
        category: row.category,
        name: form.name.trim(),
        rate: Number(form.rate) || 0,
        accountId: form.accountId || undefined,
      });
      if (!(res?.success && res.data?.success)) throw new Error(errorOf(res, "Failed to create item in Zoho"));
      toastify("success", `"${form.name.trim()}" created in Zoho Books and mapped`);
      setCreating((c) => {
        const next = { ...c };
        delete next[row.category];
        return next;
      });
      await load();
    } catch (e) {
      toastify("error", e.message);
    } finally {
      setBusy(null);
    }
  };

  const suggestedCount = categories.filter((r) => r.status === "suggested").length;

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex align-items-center mb-3">
          <div className="d-flex align-items-center justify-content-center rounded-3 me-3" style={{ width: 44, height: 44, background: "rgba(40,167,69,0.12)" }}>
            <i className="ti ti-arrows-exchange" style={{ fontSize: 24, color: "#28a745" }}></i>
          </div>
          <div className="flex-grow-1">
            <h5 className="mb-0">Zoho item mapping</h5>
            <small className="text-muted">Choose the Zoho Books item each kind of invoice posts to, so revenue lands in the right account.</small>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-light btn-sm" onClick={load} disabled={loading}>
              <i className="ti ti-refresh me-1"></i>Reload from Zoho
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleAcceptAll} disabled={bulkBusy || !suggestedCount}>
              {bulkBusy && <span className="spinner-border spinner-border-sm me-1" role="status"></span>}
              Accept all suggestions ({suggestedCount})
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm" role="status"></div>
            <div className="text-muted small mt-2">Reading items from Zoho Books...</div>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 190 }}>PayServe category</th>
                  <th style={{ width: 150 }}>Status</th>
                  <th>Zoho Books item</th>
                  <th className="text-end" style={{ width: 250 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((row) => {
                  const st = STATUS[row.status];
                  const form = creating[row.category];
                  const changed = selections[row.category] && selections[row.category] !== row.mapping?.zohoItemId;
                  return (
                    <tr key={row.category}>
                      <td>
                        <div className="fw-medium">{row.categoryLabel}</div>
                        <code className="small text-muted">{row.category}</code>
                      </td>
                      <td>
                        <span className={`badge ${st.cls}`}>{st.label}</span>
                        {row.mapping?.matchType === "created" && <div className="small text-muted mt-1">Created from PayServe</div>}
                      </td>
                      <td>
                        {form ? (
                          <div className="row g-2">
                            <div className="col-12 col-md-5">
                              <input className="form-control form-control-sm" placeholder="Item name" value={form.name} onChange={(e) => setCreating((c) => ({ ...c, [row.category]: { ...form, name: e.target.value } }))} />
                            </div>
                            <div className="col-6 col-md-3">
                              <input type="number" min="0" className="form-control form-control-sm" placeholder="Rate" value={form.rate} onChange={(e) => setCreating((c) => ({ ...c, [row.category]: { ...form, rate: e.target.value } }))} />
                            </div>
                            <div className="col-12">
                              <select className="form-select form-select-sm" value={form.accountId} onChange={(e) => setCreating((c) => ({ ...c, [row.category]: { ...form, accountId: e.target.value } }))}>
                                <option value="">Income account (Zoho default)</option>
                                {accounts.map((a) => (
                                  <option key={a.accountId} value={a.accountId}>{a.accountName}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        ) : (
                          <>
                            <select className="form-select form-select-sm" value={selections[row.category] || ""} onChange={(e) => setSelections((s) => ({ ...s, [row.category]: e.target.value }))}>
                              <option value="">Select a Zoho item...</option>
                              {items.map((i) => (
                                <option key={i.zohoItemId} value={i.zohoItemId}>
                                  {i.name}{i.accountName ? ` · ${i.accountName}` : ""}{i.status && i.status !== "active" ? " (inactive)" : ""}
                                </option>
                              ))}
                            </select>
                            {row.status === "suggested" && !row.mapping && (
                              <div className="small text-muted mt-1">Suggested: {row.suggestion.zohoItemName} ({Math.round(row.suggestion.confidence * 100)}% match)</div>
                            )}
                            {row.mapping?.accountName && <div className="small text-muted mt-1">Posts to: {row.mapping.accountName}</div>}
                          </>
                        )}
                      </td>
                      <td className="text-end">
                        {form ? (
                          <div className="d-flex gap-2 justify-content-end">
                            <button className="btn btn-sm btn-light" onClick={() => setCreating((c) => { const n = { ...c }; delete n[row.category]; return n; })}>Cancel</button>
                            <button className="btn btn-sm btn-primary" disabled={busy === row.category} onClick={() => handleCreate(row)}>
                              {busy === row.category ? <span className="spinner-border spinner-border-sm" role="status"></span> : "Create in Zoho & map"}
                            </button>
                          </div>
                        ) : (
                          <div className="d-flex gap-2 justify-content-end">
                            <button className="btn btn-sm btn-primary" disabled={busy === row.category || !changed} onClick={() => handleSave(row)}>
                              {busy === row.category ? <span className="spinner-border spinner-border-sm" role="status"></span> : "Save"}
                            </button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => openCreate(row)} title="Create a new item in Zoho Books and map this category to it">
                              <i className="ti ti-plus me-1"></i>New in Zoho
                            </button>
                            {row.mapping && (
                              <button className="btn btn-sm btn-outline-danger" disabled={busy === row.category} onClick={() => handleRemove(row)} title="Remove mapping">
                                <i className="ti ti-trash"></i>
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="text-muted small mt-3">
          Invoices synced to Zoho use these items. A category with no mapping keeps the old behaviour of matching on the line description.
        </div>
      </div>
    </div>
  );
};

export default ZohoItemMapping;
